import * as React from 'react';
import propz from 'propz';
import { FunctionComponent } from 'react';
import moment from 'moment';
import './SchoolEventDetailsSummary.css';
import { getEventGeneratedName } from '../../helpers/accessor/accessor';
import { AppUser } from 'Src/views/App/App';
import { getVenueView } from '../../helpers/venue/venue';
import { CLUB_STAFF } from '../../consts/club';
import { SchoolEvent } from '../../models/event';
import { isClubEvent } from '../../helpers/event/event';
import { getSchoolEventLocation } from '../../helpers/event/schoolEventViewHelper';
import { getSchoolEventCoaches, getSchoolEventStaff } from '../../helpers/service/admin/schoolEvents';

interface Props {
  schoolEvent: SchoolEvent;
  user: AppUser;
  eventsDetails: any;
  onEditDetails?: () => void;
}

const EVENT_ROWS = [
  { title: 'Name', field: 'name' },
  { title: 'Venue', field: 'venue' },
  { title: 'Short Event Description', field: 'shortDescription' },
  { title: 'Event Description', field: 'eventDescription' },
  { title: 'Kit notes', field: 'kitNotes' },
  { title: 'Comments', field: 'comments' },
  { title: 'Transport', field: 'transportDetails' },
  { title: 'Opposition contact details', field: 'oppositionContactDetails' },
  { title: 'Members of staff', field: 'membersOfStaff' },
  { title: 'Coach', field: 'coaches' },
  { title: 'Team departs', field: 'teamDeparts' },
  { title: 'Team returns', field: 'teamReturns' },
  { title: 'Meet time', field: 'meetTime' },
  { title: 'Tea time', field: 'teaTime' },
  { title: 'Lunch time', field: 'lunchTime' }
];

const CLUB_EVENT_ROWS = [
  { title: 'Name', field: 'name' },
  { title: 'Activity', field: 'activity' },
  { title: 'Location', field: 'location' },
  { title: 'Short Event Description', field: 'shortDescription' },
  { title: 'Event Description', field: 'eventDescription' },
  { title: 'Clothing/Kit notes', field: 'kitNotes' },
  { title: 'Comments', field: 'comments' },
  { title: 'Leader/Coach', field: 'coaches' },
  { title: 'Members of staff', field: 'membersOfStaff' },
  { title: 'Group departs', field: 'teamDeparts' },
  { title: 'Group returns', field: 'teamReturns' },
  { title: 'Meet time', field: 'meetTime' },
  { title: 'Tea time', field: 'teaTime' },
  { title: 'Lunch time', field: 'lunchTime' }
];

export const SchoolEventDetailsSummary: FunctionComponent<Props> = props => {
  const { eventsDetails, schoolEvent, user, onEditDetails } = props;
  const ROWS = isClubEvent(schoolEvent) ? [...CLUB_EVENT_ROWS] : [...EVENT_ROWS];
  const [staffDetailsById, setStaffDetailsById] = React.useState({});

  const getStaffName = staff => {
    const firstName = propz.get(staff, ['firstName'], '');
    const lastName = propz.get(staff, ['lastName'], '');
    return `${lastName} ${firstName}`.trim();
  };

  const getStaffInitials = staff => {
    const nameParts = getStaffName(staff)
      .split(/\s+/)
      .filter(Boolean);

    if (nameParts.length === 0) {
      return '?';
    }

    return nameParts
      .slice(0, 2)
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  };

  const getStaffAvatarUrl = staff => {
    const imageSrc =
      propz.get(staff, ['profile', 'avatar'], '') ||
      propz.get(staff, ['user', 'profile', 'avatar'], '') ||
      propz.get(staff, ['userProfile', 'avatar'], '') ||
      propz.get(staff, ['profileData', 'avatar'], '') ||
      propz.get(staff, ['avatar'], '') ||
      propz.get(staff, ['avatarUrl'], '') ||
      propz.get(staff, ['photoUrl'], '') ||
      propz.get(staff, ['pic'], '') ||
      propz.get(staff, ['photo'], '') ||
      propz.get(staff, ['picture'], '') ||
      propz.get(staff, ['image'], '') ||
      propz.get(staff, ['imageUrl'], '');

    return imageSrc && String(imageSrc).startsWith('//') ? `https:${imageSrc}` : imageSrc;
  };

  const getStaffKey = staff => {
    return (
      propz.get(staff, ['id'], '') ||
      propz.get(staff, ['_id'], '') ||
      propz.get(staff, ['userId'], '') ||
      propz.get(staff, ['permissionId'], '') ||
      getStaffName(staff)
    );
  };

  const getStaffKeys = staff => {
    return [
      propz.get(staff, ['id'], ''),
      propz.get(staff, ['_id'], ''),
      propz.get(staff, ['userId'], ''),
      propz.get(staff, ['permissionId'], ''),
      getStaffName(staff)
    ].filter(Boolean);
  };

  const getStaffIdKeys = staff => {
    return [
      propz.get(staff, ['id'], ''),
      propz.get(staff, ['_id'], ''),
      propz.get(staff, ['userId'], ''),
      propz.get(staff, ['permissionId'], '')
    ].filter(Boolean);
  };

  React.useEffect(() => {
    const staffItems = propz.get(eventsDetails, ['staff'], []);
    const coaches = staffItems.filter(staff => staff.staffRole === CLUB_STAFF.COACH);
    const membersOfStaff = staffItems.filter(staff => staff.staffRole === CLUB_STAFF.MEMBER_OF_STAFF);
    const getIds = items =>
      items.reduce((ids, item) => {
        getStaffIdKeys(item).forEach(id => {
          if (ids.indexOf(id) === -1) {
            ids.push(id);
          }
        });
        return ids;
      }, []);
    const coachIds = getIds(coaches);
    const memberOfStaffIds = getIds(membersOfStaff);
    const filter = ids => ({
      where: {
        _id: {
          $in: ids
        }
      },
      limit: ids.length,
      skip: 0
    });
    const requests = [];

    if (coachIds.length > 0) {
      requests.push(getSchoolEventCoaches(user, schoolEvent.id, filter(coachIds)));
    }

    if (memberOfStaffIds.length > 0) {
      requests.push(getSchoolEventStaff(user, schoolEvent.id, filter(memberOfStaffIds)));
    }

    if (requests.length === 0) {
      setStaffDetailsById({});
      return;
    }

    Promise.all(requests)
      .then(results => {
        const detailsById = {};
        results.forEach(items => {
          items.forEach(item => {
            getStaffKeys(item).forEach(key => {
              detailsById[key] = item;
            });
          });
        });
        setStaffDetailsById(detailsById);
      })
      .catch(error => {
        console.error('Can not load event staff details', error);
      });
  }, [eventsDetails, schoolEvent.id]);

  const getStaffWithDetails = staff => {
    const staffDetails = getStaffKeys(staff)
      .map(key => staffDetailsById[key])
      .find(Boolean);

    return staffDetails ? { ...staffDetails, ...staff, avatar: getStaffAvatarUrl(staffDetails) } : staff;
  };

  const renderStaffChips = staffItems => {
    if (staffItems.length === 0) {
      return '';
    }

    return (
      <div className="eEventDetailsSummary__staffChips">
        {staffItems.map(staffItem => {
          const staff = getStaffWithDetails(staffItem);
          const staffName = getStaffName(staff);
          const avatarUrl = getStaffAvatarUrl(staff);

          return (
            <div className="eEventDetailsSummaryStaffChip" key={getStaffKey(staff)}>
              <div className="eEventDetailsSummaryStaffChip__avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={staffName} className="eEventDetailsSummaryStaffChip__avatarImg" />
                ) : (
                  <div className="eEventDetailsSummaryStaffChip__avatarFallback">{getStaffInitials(staff)}</div>
                )}
              </div>
              <div className="eEventDetailsSummaryStaffChip__name" title={staffName}>
                {staffName}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getContentCell = row => {
    let contentCell: any;
    const field = row.field;
    const defaultDate = new Date();
    defaultDate.setHours(0);
    defaultDate.setMinutes(0);
    const location = propz.get(schoolEvent, ['location'], '');
    const { activeSchoolId } = user;

    switch (field) {
      case 'name':
        contentCell = getEventGeneratedName(schoolEvent, { user: user });
        break;
      case 'activity':
        contentCell = propz.get(schoolEvent, ['sport', 'name'], '');
        break;
      case 'venue':
        contentCell = getVenueView(schoolEvent, activeSchoolId);
        break;
      case 'location':
        contentCell =
          location !== ''
            ? `${getSchoolEventLocation(schoolEvent, user)},  ${location}`
            : getSchoolEventLocation(schoolEvent, user);
        break;
      case 'eventDescription':
        const description = propz.get(eventsDetails, ['description'], '');
        contentCell = <div dangerouslySetInnerHTML={{ __html: description }} />;
        break;
      case 'shortDescription':
        contentCell = propz.get(eventsDetails, ['shortDescription'], '');
        break;
      case 'kitNotes':
        const kitNotes = propz.get(eventsDetails, ['kitNotes'], '');
        contentCell = <div dangerouslySetInnerHTML={{ __html: kitNotes }} />;
        break;
      case 'comments':
        const comments = propz.get(eventsDetails, ['comments'], '');
        contentCell = <div dangerouslySetInnerHTML={{ __html: comments }} />;
        break;
      case 'transportDetails':
        const transportDetails = propz.get(eventsDetails, ['transportDetails'], '');
        contentCell = <div dangerouslySetInnerHTML={{ __html: transportDetails }} />;
        break;
      case 'oppositionContactDetails':
        const oppositionContactDetails = propz.get(eventsDetails, ['oppositionContactDetails'], '');
        contentCell = <div dangerouslySetInnerHTML={{ __html: oppositionContactDetails }} />;
        break;
      case 'membersOfStaff':
        const staffs = propz
          .get(eventsDetails, ['staff'], [])
          .filter(staff => staff.staffRole === CLUB_STAFF.MEMBER_OF_STAFF);

        contentCell = renderStaffChips(staffs);
        break;
      case 'coaches':
        const coaches = propz.get(eventsDetails, ['staff'], []).filter(coach => coach.staffRole === CLUB_STAFF.COACH);

        contentCell = renderStaffChips(coaches);
        break;
      case 'teamDeparts':
        const departsDate = new Date(propz.get(eventsDetails, ['teamDeparts'], defaultDate));
        const departsTime = moment(departsDate, 'hh:mm A').format('hh:mm A');
        contentCell = typeof eventsDetails?.teamDeparts !== 'undefined' ? departsTime : '--:--';
        break;
      case 'teamReturns':
        const returnsDate = new Date(propz.get(eventsDetails, ['teamReturns'], defaultDate));
        const returnsTime = moment(returnsDate, 'hh:mm A').format('hh:mm A');
        contentCell = typeof eventsDetails?.teamReturns !== 'undefined' ? returnsTime : '--:--';
        break;
      case 'meetTime':
        const meetDate = new Date(propz.get(eventsDetails, ['meetTime'], defaultDate));
        const meetTime = moment(meetDate, 'hh:mm A').format('hh:mm A');
        contentCell = typeof eventsDetails?.meetTime !== 'undefined' ? meetTime : '--:--';
        break;
      case 'teaTime':
        const teaDate = new Date(propz.get(eventsDetails, ['teaTime'], defaultDate));
        const teaTime = moment(teaDate, 'hh:mm A').format('hh:mm A');
        contentCell = typeof eventsDetails?.teaTime !== 'undefined' ? teaTime : '--:--';
        break;
      case 'lunchTime':
        const lunchDate = new Date(propz.get(eventsDetails, ['lunchTime'], defaultDate));
        const lunchTime = moment(lunchDate, 'hh:mm A').format('hh:mm A');
        contentCell = typeof eventsDetails?.lunchTime !== 'undefined' ? lunchTime : '--:--';
        break;
      default:
        contentCell = schoolEvent[row.field];
    }

    return contentCell;
  };

  const isEmptyValue = value => {
    return value === undefined || value === null || value === '';
  };

  const renderField = (row, index) => {
    const contentCell = getContentCell(row);
    const isEmpty = isEmptyValue(contentCell);

    return (
      <div className="eEventDetailsSummary__row" key={`event_details_summary_${row.field}_${index}`}>
        <div className="eEventDetailsSummary__label">{row.title}</div>
        <div
          className={
            isEmpty ? 'eEventDetailsSummary__value eEventDetailsSummary__value--empty' : 'eEventDetailsSummary__value'
          }
        >
          {isEmpty ? 'Not set' : contentCell}
        </div>
      </div>
    );
  };

  const renderSection = (title: string, fields: string[], action?: React.ReactNode) => {
    const sectionRows = ROWS.filter(row => fields.indexOf(row.field) !== -1);

    return (
      <section className="eEventDetailsSummary__card">
        <div className="eEventDetailsSummary__cardHeader">
          <div className="eEventDetailsSummary__cardTitle">
            <h3>{title}</h3>
          </div>
          {action}
        </div>
        <div className="eEventDetailsSummary__list">{sectionRows.map(renderField)}</div>
      </section>
    );
  };

  const editAction = onEditDetails ? (
    <button type="button" className="eEventDetailsSummary__editButton" onClick={onEditDetails}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
      </svg>
      <span>Edit details</span>
    </button>
  ) : null;

  return (
    <div className="eEventDetailsSummary">
      {renderSection('Event details', ['name', 'venue', 'location', 'activity'], editAction)}
      {renderSection('Notes', [
        'shortDescription',
        'eventDescription',
        'kitNotes',
        'comments',
        'transportDetails',
        'oppositionContactDetails'
      ])}
      {renderSection('People', ['membersOfStaff', 'coaches'])}
      {renderSection('Times', ['teamDeparts', 'teamReturns', 'meetTime', 'teaTime', 'lunchTime'])}
    </div>
  );
};

SchoolEventDetailsSummary.displayName = 'SchoolEventDetailsSummary';
